import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, Printer, EyeOff, Eye, ArrowRight, Wrench, Users, Clock, CheckCircle2, Car, Camera, Heart } from "lucide-react";

const STATUS_TABS = [
  { key: "all", label: "الكل" },
  { key: "approved", label: "المعتمدين" },
  { key: "pending", label: "قيد المراجعة" },
];

const MODES = [
  { key: "participants", label: "قائمة الأعضاء", icon: Users },
  { key: "not_ready", label: "السيارات غير الجاهزة", icon: Wrench },
];

const TYPE_TABS = [
  { key: "all", label: "كل الأنواع", icon: Users },
  { key: "car_owner", label: "مالك سيارة", icon: Car },
  { key: "photographer", label: "مصوّر", icon: Camera },
  { key: "fan", label: "هاوي", icon: Heart },
];

const TYPE_LABEL = {
  car_owner: "مالك سيارة",
  photographer: "مصوّر",
  fan: "هاوي",
};

export default function AdminExport() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("approved");
  const [typeFilter, setTypeFilter] = useState("all");
  const [hideMobile, setHideMobile] = useState(false);
  const [mode, setMode] = useState("participants");
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("rb100_admin_token")) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const params = filter === "all" ? {} : { status_filter: filter };
        const res = await api.get("/registrations", { params });
        setItems(res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [filter, navigate]);

  let visibleItems = items;
  if (mode === "not_ready") {
    visibleItems = visibleItems.filter((r) => (r.membership_type || "car_owner") === "car_owner" && r.car_ready === false);
  }
  if (typeFilter !== "all") {
    visibleItems = visibleItems.filter((r) => (r.membership_type || "car_owner") === typeFilter);
  }

  const maskMobile = (m) => {
    if (!hideMobile) return m;
    if (!m) return "—";
    const s = String(m);
    if (s.length <= 4) return "••••";
    return s.slice(0, 4) + "•••••" + s.slice(-2);
  };

  const today = new Date().toLocaleDateString("ar-SA", {
    day: "numeric", month: "long", year: "numeric",
  });

  const isNotReadyMode = mode === "not_ready";

  return (
    <div data-testid="admin-export" className="min-h-screen bg-white text-rb-ink">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden bg-white border-b border-rb-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              data-testid="export-back-btn"
              className="flex items-center gap-2 text-sm text-rb-textMuted hover:text-rb-ink"
            >
              <ArrowRight className="w-4 h-4" />
              الرجوع للوحة
            </Link>
            <div className="h-5 w-px bg-rb-border" />
            <h1 className="font-heading text-lg font-extrabold text-rb-ink">
              تصدير {isNotReadyMode ? "السيارات غير الجاهزة" : "قائمة الأعضاء"}
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Mode switcher */}
            <div className="flex border border-rb-border bg-rb-surface">
              {MODES.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.key}
                    onClick={() => setMode(m.key)}
                    data-testid={`export-mode-${m.key}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors ${
                      mode === m.key
                        ? "bg-rb-ink text-white"
                        : "text-rb-textMuted hover:text-rb-ink"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {m.label}
                  </button>
                );
              })}
            </div>

            <div className="flex border border-rb-border bg-rb-surface">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  data-testid={`export-filter-${t.key}`}
                  className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                    filter === t.key
                      ? "bg-rb-ink text-white"
                      : "text-rb-textMuted hover:text-rb-ink"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Membership type filter */}
            {mode === "participants" && (
              <div className="flex border border-rb-border bg-rb-surface">
                {TYPE_TABS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setTypeFilter(t.key)}
                      data-testid={`export-type-${t.key}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors ${
                        typeFilter === t.key
                          ? "bg-rb-red text-white"
                          : "text-rb-textMuted hover:text-rb-ink"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setHideMobile((v) => !v)}
              data-testid="export-toggle-mobile"
              className={`flex items-center gap-2 border px-3 py-1.5 text-xs font-bold transition-colors ${
                hideMobile
                  ? "border-rb-red text-rb-red bg-rb-red/5"
                  : "border-rb-border text-rb-ink bg-white hover:border-rb-ink"
              }`}
            >
              {hideMobile ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {hideMobile ? "إظهار رقم الجوال" : "إخفاء رقم الجوال"}
            </button>

            <button
              onClick={() => window.print()}
              data-testid="export-print-btn"
              className="flex items-center gap-2 bg-rb-red hover:bg-rb-redHover text-white px-4 py-2 text-sm font-bold transition-colors"
            >
              <Printer className="w-4 h-4" />
              تصدير PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable area */}
      <main className="max-w-7xl mx-auto px-6 py-8 print:p-0 print:max-w-full">
        {/* Document header */}
        <div className="mb-6 border-b border-rb-border pb-4">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] text-rb-red font-bold mb-1">
                RB100 · CLUB MEMBERSHIP
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-rb-ink">
                {isNotReadyMode
                  ? "السيارات غير الجاهزة — متابعة الجاهزية"
                  : "قائمة أعضاء النادي — كلاسيك ومعدّلة"}
              </h2>
              <p className="text-xs text-rb-textMuted mt-1">
                نادي RB100 · المدينة المنورة · منذ ٢٠٠٢
              </p>
            </div>
            <div className="text-left text-xs text-rb-textMuted">
              <div>تاريخ التصدير: {today}</div>
              <div>
                {isNotReadyMode ? "عدد السيارات" : "عدد الأعضاء"}:{" "}
                <span className="font-bold text-rb-ink">{visibleItems.length}</span>
              </div>
              {hideMobile && (
                <div className="mt-1 text-rb-red font-bold">⚠️ أرقام الجوال مُخفاة</div>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="w-7 h-7 animate-spin text-rb-red" />
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="py-20 text-center text-rb-textMuted">
            {isNotReadyMode ? "لا توجد سيارات غير جاهزة." : "لا توجد مشاركات للتصدير."}
          </div>
        ) : isNotReadyMode ? (
          /* Not-ready cars — card-based detailed layout */
          <div className="space-y-4 print:space-y-3">
            {visibleItems.map((r, i) => (
              <article
                key={r.id}
                data-testid={`notready-card-${r.reference_code}`}
                className="border border-rb-border print:border-rb-ink/40 break-inside-avoid"
              >
                <header className="bg-rb-ink text-white px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-white/60">#{i + 1}</span>
                    <span className="font-bold text-base">{r.name}</span>
                    <span className="font-mono text-xs text-white/70">
                      {r.reference_code}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                    <span className="font-bold">
                      {r.expected_ready_date
                        ? `جاهزة بتاريخ: ${r.expected_ready_date}`
                        : "موعد الجاهزية غير محدّد"}
                    </span>
                  </div>
                </header>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2.5 p-4 text-sm">
                  <Cell label="رقم الجوال" value={maskMobile(r.mobile)} mono ltr />
                  <Cell label="رقم اللوحة" value={r.plate_number} mono bold ltr />
                  <Cell label="الماركة" value={r.car_make || "—"} />
                  <Cell label="الموديل" value={r.car_model || "—"} />
                  <Cell label="السنة" value={r.car_year || "—"} />
                  <Cell
                    label="الحالة"
                    value={
                      r.status === "approved" ? "معتمد"
                      : r.status === "pending" ? "قيد المراجعة"
                      : "مرفوض"
                    }
                  />
                  <Cell label="البريد" value={r.email} ltr />
                </div>

                {r.notes && (
                  <div className="border-t border-rb-border bg-rb-surface print:bg-white px-4 py-3">
                    <div className="text-[10px] tracking-widest text-rb-textMuted uppercase mb-1">
                      ملاحظات العضو
                    </div>
                    <div className="text-xs text-rb-ink whitespace-pre-line">
                      {r.notes}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        ) : (
          /* Participants — table */
          <div className="overflow-x-auto">
            <table
              data-testid="export-table"
              className="w-full text-sm border border-rb-border print:text-xs"
            >
              <thead className="bg-rb-ink text-white">
                <tr>
                  <th className="px-2 py-2 text-right border-l border-white/20 w-10">#</th>
                  <th className="px-3 py-2 text-right border-l border-white/20">الاسم</th>
                  <th className="px-3 py-2 text-right border-l border-white/20">النوع</th>
                  <th className="px-3 py-2 text-right border-l border-white/20">رقم الجوال</th>
                  <th className="px-3 py-2 text-right border-l border-white/20">رقم اللوحة</th>
                  <th className="px-3 py-2 text-right border-l border-white/20">السيارة</th>
                  <th className="px-3 py-2 text-right border-l border-white/20">حسابات/جاهزية</th>
                  <th className="px-3 py-2 text-right">رقم المرجع</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((r, i) => {
                  const mtype = r.membership_type || "car_owner";
                  const isCO = mtype === "car_owner";
                  const isPh = mtype === "photographer";
                  return (
                  <tr
                    key={r.id}
                    className={i % 2 === 0 ? "bg-white" : "bg-rb-surface print:bg-white"}
                  >
                    <td className="px-2 py-2 border-l border-rb-border text-rb-textMuted">
                      {i + 1}
                    </td>
                    <td className="px-3 py-2 border-l border-rb-border font-bold text-rb-ink">
                      {r.name}
                    </td>
                    <td className="px-3 py-2 border-l border-rb-border text-xs">
                      <span className={`inline-block px-2 py-0.5 border ${
                        mtype === "car_owner" ? "border-zinc-300 bg-zinc-50 text-zinc-700"
                        : mtype === "photographer" ? "border-amber-300 bg-amber-50 text-amber-800"
                        : "border-rose-300 bg-rose-50 text-rose-700"
                      }`}>
                        {TYPE_LABEL[mtype] || mtype}
                      </span>
                    </td>
                    <td
                      className="px-3 py-2 border-l border-rb-border font-mono text-rb-ink"
                      dir="ltr"
                      style={{ textAlign: "right" }}
                    >
                      {maskMobile(r.mobile)}
                    </td>
                    <td
                      className="px-3 py-2 border-l border-rb-border font-mono font-bold text-rb-ink"
                      dir="ltr"
                      style={{ textAlign: "right" }}
                    >
                      {isCO ? (r.plate_number || "—") : "—"}
                    </td>
                    <td className="px-3 py-2 border-l border-rb-border text-rb-ink text-xs">
                      {isCO
                        ? ([r.car_year, r.car_make, r.car_model].filter(Boolean).join(" · ") || "—")
                        : "—"}
                    </td>
                    <td className="px-3 py-2 border-l border-rb-border text-xs" dir="ltr" style={{ textAlign: "right" }}>
                      {isCO ? (
                        r.car_ready === false ? (
                          <span className="text-amber-700 font-bold">
                            {r.expected_ready_date ? `جاهزة: ${r.expected_ready_date}` : "غير جاهزة"}
                          </span>
                        ) : (
                          <span className="text-green-700 font-bold">جاهزة ✓</span>
                        )
                      ) : isPh ? (
                        <div className="space-y-0.5">
                          {r.instagram_handle && <div>IG: @{r.instagram_handle}</div>}
                          {r.snapchat_handle && <div>SC: @{r.snapchat_handle}</div>}
                          {!r.instagram_handle && !r.snapchat_handle && <span className="text-rb-textMuted">—</span>}
                        </div>
                      ) : (
                        <span className="text-rb-textMuted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-rb-textMuted">
                      {r.reference_code}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer signature row — only visible on print */}
        <div className="hidden print:block mt-8 pt-4 border-t border-rb-border text-[10px] text-rb-textMuted flex items-center justify-between">
          <div>RB100 · نادي السيارات الكلاسيكية والمعدّلة · المدينة المنورة</div>
          <div>rb100.net</div>
        </div>
      </main>

      <style>{`
        @media print {
          @page {
            size: A4 ${isNotReadyMode ? "portrait" : "landscape"};
            margin: 12mm;
          }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print\\:hidden { display: none !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          .break-inside-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

const Cell = ({ label, value, mono, bold, ltr }) => (
  <div>
    <div className="text-[10px] tracking-widest text-rb-textMuted uppercase mb-1">
      {label}
    </div>
    <div
      className={`text-rb-ink ${mono ? "font-mono" : ""} ${bold ? "font-bold" : ""}`}
      dir={ltr ? "ltr" : undefined}
      style={ltr ? { textAlign: "right" } : undefined}
    >
      {value}
    </div>
  </div>
);
